import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  activityApi,
  adminApi,
  TenantActivityStats,
  AdminTenantActivityStats,
  AdminTenant,
  ActivityEvent,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../Toast";
import { formatActivityDate, formatFullActivityDate, parseServerDate } from "../../utils/formatters";
import TimelineIcon from "@mui/icons-material/Timeline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LanguageIcon from "@mui/icons-material/Language";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import "./ActivityDashboard.css";

const PAGE_SIZE = 30;

// MUI icon + background colour per event type
const EVENT_ICON_MAP: Record<string, { icon: React.ReactNode; color: string }> = {
  cluster_created: { icon: <BubbleChartIcon fontSize="small" />, color: "#38a169" },
  cluster_refreshed: { icon: <RefreshIcon fontSize="small" />, color: "#3182ce" },
  cluster_deleted: { icon: <DeleteOutlineIcon fontSize="small" />, color: "#c53030" },
  keywords_generated: { icon: <VpnKeyIcon fontSize="small" />, color: "#d69e2e" },
  domain_created: { icon: <LanguageIcon fontSize="small" />, color: "#319795" },
  domain_deleted: { icon: <RemoveCircleOutlineIcon fontSize="small" />, color: "#e53e3e" },
  global_keywords_refresh: { icon: <RefreshIcon fontSize="small" />, color: "#6b46c1" },
  global_keywords_refresh_rejected: { icon: <RemoveCircleOutlineIcon fontSize="small" />, color: "#c05621" },
};

const DEFAULT_EVENT_ICON = { icon: <TimelineIcon fontSize="small" />, color: "#718096" };

// ─── Sub-components ───

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color = "#667eea" }) => (
  <div className="activity-stat-card">
    <div className="activity-stat-icon" style={{ background: color }}>
      {icon}
    </div>
    <div className="activity-stat-body">
      <div className="activity-stat-value">{value}</div>
      <div className="activity-stat-label">{label}</div>
    </div>
  </div>
);

/** Renders the stat-card grid from aggregated stats. */
const StatsGrid: React.FC<{ stats: TenantActivityStats }> = ({ stats }) => (
  <div className="activity-stats-grid">
    <StatCard icon={<BubbleChartIcon fontSize="small" />}           label="Clusters Created"   value={stats.clusters_created}           color="#38a169" />
    <StatCard icon={<RefreshIcon fontSize="small" />}               label="Clusters Refreshed"  value={stats.clusters_refreshed}        color="#3182ce" />
    <StatCard icon={<VpnKeyIcon fontSize="small" />}                label="Keyword Events"      value={stats.keywords_generated_events} color="#d69e2e" />
    <StatCard icon={<TextSnippetIcon fontSize="small" />}           label="Total Keywords"      value={stats.total_keywords_generated}  color="#805ad5" />
    <StatCard icon={<LanguageIcon fontSize="small" />}              label="Domains Created"     value={stats.domains_created}            color="#319795" />
    <StatCard icon={<DeleteOutlineIcon fontSize="small" />}         label="Clusters Deleted"    value={stats.clusters_deleted}           color="#c53030" />
    <StatCard
      icon={<AccessTimeIcon fontSize="small" />}
      label="Last Activity"
      value={formatActivityDate(stats.last_activity_at)}
      color="#718096"
    />
  </div>
);

/** Summarise the "detail" JSON blob into a human-readable string. */
const describeDetail = (event: ActivityEvent): string => {
  const d = event.detail;
  if (!d) return "";
  const parts: string[] = [];

  if (event.event_type === "global_keywords_refresh") {
    // Global refresh — show cost-relevant stats
    if (d.triggered_by) parts.push(d.triggered_by === "admin" ? "Manual" : "Scheduled");
    if (d.total_keywords_fetched != null) parts.push(`${d.total_keywords_fetched} kws fetched`);
    if (d.clusters_refreshed != null) parts.push(`${d.clusters_refreshed} clusters`);
    if (d.domains_scanned != null) parts.push(`${d.domains_scanned} domains`);
    if (d.duration_s != null) parts.push(`${d.duration_s}s`);
    if (d.errors) parts.push(`${d.errors} error${d.errors !== 1 ? "s" : ""}`);
    if (d.sandbox_mode) parts.push("[SANDBOX]");
  } else if (event.event_type === "global_keywords_refresh_rejected") {
    // Rejected refresh — show who tried and when the last refresh ran
    if (d.triggered_by) parts.push(d.triggered_by === "admin" ? "Manual attempt" : "Scheduled attempt");
    if (d.last_refresh_at) parts.push(`Last refresh: ${parseServerDate(d.last_refresh_at as string).toLocaleString()}`);
  } else {
    // Per-cluster and domain events
    if (d.anchor_keyword) parts.push(`"${d.anchor_keyword}"`);
    if (d.domain_name) parts.push(`domain "${d.domain_name}"`);
    if (d.keyword_count) parts.push(`${d.keyword_count} keywords`);
    if (d.keywords_with_data) parts.push(`${d.keywords_with_data} with data`);
    if (d.still_pending) parts.push(`${d.still_pending} pending`);
  }

  return parts.join(" · ");
};

/** Renders a list of ActivityEvents. showUser controls user attribution. */
const EventList: React.FC<{
  events: ActivityEvent[];
  isLoading: boolean;
  showUser: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}> = ({ events, isLoading, showUser, onLoadMore, hasMore }) => {
  if (isLoading && events.length === 0) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading events...</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="admin-empty" style={{ padding: "24px 0" }}>
        <TimelineIcon className="admin-empty-icon" sx={{ fontSize: 36, color: "#a0aec0" }} />
        <p style={{ color: "#718096", margin: "8px 0 0" }}>No activity events yet.</p>
      </div>
    );
  }

  return (
    <div className="activity-event-list">
      {events.map((ev) => {
        const { icon, color } = EVENT_ICON_MAP[ev.event_type] || DEFAULT_EVENT_ICON;
        const detail = describeDetail(ev);
        return (
          <div key={ev.id} className="activity-event-row">
            <div className="activity-event-icon" style={{ background: color }}>
              {icon}
            </div>
            <div className="activity-event-body">
              <div className="activity-event-title">
                <span className="activity-event-label">{ev.event_label}</span>
                {detail && (
                  <span className="activity-event-detail">{detail}</span>
                )}
              </div>
              <div className="activity-event-meta">
                {showUser && ev.user_email && (
                  <>
                    <span className="activity-event-user">{ev.user_email}</span>
                    <span className="activity-event-sep">·</span>
                  </>
                )}
                <span className="activity-event-time" title={formatFullActivityDate(ev.created_at)}>
                  {formatActivityDate(ev.created_at)}
                </span>
              </div>
            </div>
          </div>
        );
      })}

      {hasMore && (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <button className="admin-action-btn" onClick={onLoadMore} disabled={isLoading}>
            {isLoading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───

const ActivityDashboard: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isSuperadmin = user?.is_superadmin ?? false;

  // ── Own tenant data ──
  const [myStats, setMyStats] = useState<TenantActivityStats | null>(null);
  const [myEvents, setMyEvents] = useState<ActivityEvent[]>([]);
  const [isLoadingMy, setIsLoadingMy] = useState(true);
  const [isLoadingMyEvents, setIsLoadingMyEvents] = useState(true);
  const [myEventsOffset, setMyEventsOffset] = useState(0);
  const [myHasMore, setMyHasMore] = useState(true);

  // ── Superadmin: tenants + inline expand ──
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [expandedTenantId, setExpandedTenantId] = useState<string | null>(null);
  const [expandedStats, setExpandedStats] = useState<AdminTenantActivityStats | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<ActivityEvent[]>([]);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);
  const [isLoadingExpandedEvents, setIsLoadingExpandedEvents] = useState(false);
  const [expandedEventsOffset, setExpandedEventsOffset] = useState(0);
  const [expandedHasMore, setExpandedHasMore] = useState(true);

  const hasFetchedRef = useRef(false);

  // ── Load own activity ──
  const loadMyActivity = useCallback(async () => {
    setIsLoadingMy(true);
    try {
      const stats = await activityApi.getMyActivity();
      setMyStats(stats);
    } catch (err) {
      console.error("Failed to load activity:", err);
    } finally {
      setIsLoadingMy(false);
    }
  }, []);

  const loadMyEvents = useCallback(async (offset = 0, append = false) => {
    setIsLoadingMyEvents(true);
    try {
      const resp = await activityApi.getMyActivityEvents(PAGE_SIZE, offset);
      if (append) {
        setMyEvents((prev) => [...prev, ...resp.events]);
      } else {
        setMyEvents(resp.events);
      }
      setMyEventsOffset(offset + resp.events.length);
      setMyHasMore(resp.events.length >= PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load activity events:", err);
    } finally {
      setIsLoadingMyEvents(false);
    }
  }, []);

  // ── Superadmin: load tenants ──
  const loadTenants = useCallback(async () => {
    if (!isSuperadmin) return;
    setIsLoadingTenants(true);
    try {
      const data = await adminApi.listTenants();
      setTenants(data);
    } catch {
      showToast("error", "Failed to load tenants");
    } finally {
      setIsLoadingTenants(false);
    }
  }, [isSuperadmin, showToast]);

  // ── Superadmin: toggle inline expand ──
  const handleToggleTenant = useCallback(
    async (tenantId: string) => {
      if (expandedTenantId === tenantId) {
        setExpandedTenantId(null);
        setExpandedStats(null);
        setExpandedEvents([]);
        return;
      }

      setExpandedTenantId(tenantId);
      setExpandedStats(null);
      setExpandedEvents([]);
      setExpandedEventsOffset(0);
      setExpandedHasMore(true);
      setIsLoadingExpanded(true);
      setIsLoadingExpandedEvents(true);

      try {
        const [stats, eventsResp] = await Promise.all([
          activityApi.getTenantActivity(tenantId),
          activityApi.getTenantActivityEvents(tenantId, PAGE_SIZE, 0),
        ]);
        setExpandedStats(stats);
        setExpandedEvents(eventsResp.events);
        setExpandedEventsOffset(eventsResp.events.length);
        setExpandedHasMore(eventsResp.events.length >= PAGE_SIZE);
      } catch {
        showToast("error", "Failed to load tenant activity");
      } finally {
        setIsLoadingExpanded(false);
        setIsLoadingExpandedEvents(false);
      }
    },
    [expandedTenantId, showToast]
  );

  const loadMoreExpandedEvents = useCallback(async () => {
    if (!expandedTenantId) return;
    setIsLoadingExpandedEvents(true);
    try {
      const resp = await activityApi.getTenantActivityEvents(
        expandedTenantId,
        PAGE_SIZE,
        expandedEventsOffset
      );
      setExpandedEvents((prev) => [...prev, ...resp.events]);
      setExpandedEventsOffset((prev) => prev + resp.events.length);
      setExpandedHasMore(resp.events.length >= PAGE_SIZE);
    } catch {
      showToast("error", "Failed to load more events");
    } finally {
      setIsLoadingExpandedEvents(false);
    }
  }, [expandedTenantId, expandedEventsOffset, showToast]);

  // ── Initial fetch ──
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      loadMyActivity();
      loadMyEvents(0);
      if (isSuperadmin) loadTenants();
    }
  }, [loadMyActivity, loadMyEvents, loadTenants, isSuperadmin]);

  // ════════════════════════════════════════
  // TENANT ADMIN VIEW — full detail + user attribution
  // ════════════════════════════════════════
  if (!isSuperadmin) {
    return (
      <div>
        <div className="admin-section-header">
          <h2>Activity Tracking</h2>
        </div>

        {isLoadingMy ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
            <p>Loading activity data...</p>
          </div>
        ) : myStats ? (
          <StatsGrid stats={myStats} />
        ) : (
          <div className="admin-empty">
            <TimelineIcon className="admin-empty-icon" sx={{ fontSize: 48 }} />
            <p>No activity data available yet.</p>
          </div>
        )}

        <div className="activity-section" style={{ marginTop: 28 }}>
          <h3 className="activity-section-title">Recent Activity</h3>
          <EventList
            events={myEvents}
            isLoading={isLoadingMyEvents}
            showUser={true}
            hasMore={myHasMore}
            onLoadMore={() => loadMyEvents(myEventsOffset, true)}
          />
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // SUPERADMIN VIEW — aggregated, privacy-respecting
  // ════════════════════════════════════════
  return (
    <div>
      <div className="admin-section-header">
        <h2>Activity Tracking</h2>
      </div>

      {/* Own tenant stats */}
      <div className="activity-section">
        <h3 className="activity-section-title">My Tenant Activity</h3>
        {isLoadingMy ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : myStats ? (
          <StatsGrid stats={myStats} />
        ) : (
          <p className="activity-no-data">No activity data.</p>
        )}
      </div>

      {/* Per-tenant breakdown with inline expand */}
      <div className="activity-section" style={{ marginTop: 32 }}>
        <h3 className="activity-section-title">Tenant Activity Overview</h3>

        {isLoadingTenants ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
            <p>Loading tenants...</p>
          </div>
        ) : tenants.length === 0 ? (
          <p className="activity-no-data">No tenants found.</p>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table activity-tenant-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Last Activity</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => {
                  const isExpanded = expandedTenantId === t.id;
                  return (
                    <React.Fragment key={t.id}>
                      <tr
                        className={`activity-tenant-row ${isExpanded ? "expanded-row" : ""}`}
                        onClick={() => handleToggleTenant(t.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{t.name}</td>
                        <td>
                          {t.subscription_plan_name ? (
                            <span className={`plan-badge ${t.subscription_plan_name.toLowerCase()}`}>
                              {t.subscription_plan_name}
                            </span>
                          ) : (
                            <span className="no-plan-badge">No plan</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${t.is_active ? "badge-active" : "badge-inactive"}`}>
                            {t.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="activity-last-col">
                          {formatActivityDate(t.last_activity_at ?? null)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </td>
                      </tr>

                      {/* ── Inline expanded detail (privacy: no user info) ── */}
                      {isExpanded && (
                        <tr className="activity-inline-detail-row">
                          <td colSpan={5}>
                            <div className="activity-inline-detail">
                              {isLoadingExpanded ? (
                                <div className="admin-loading">
                                  <div className="admin-loading-spinner"></div>
                                  <p>Loading details...</p>
                                </div>
                              ) : expandedStats ? (
                                <>
                                  <StatsGrid stats={expandedStats} />
                                  <h4 style={{ margin: "20px 0 8px" }}>Recent Events</h4>
                                  <EventList
                                    events={expandedEvents}
                                    isLoading={isLoadingExpandedEvents}
                                    showUser={false}
                                    hasMore={expandedHasMore}
                                    onLoadMore={loadMoreExpandedEvents}
                                  />
                                </>
                              ) : (
                                <p className="activity-no-data">No data.</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityDashboard;
